import { useEffect, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  fetchMessagesForRoom,
  fetchNewMessagesForRoom,
  subscribeToMessagesForRoom,
} from "./ChatThunk";
import * as chatV2 from "../../../modules/chat/redux/ChatSlice.v2";
import { RootState } from "src/setup/redux/store";
import { Room } from "src/app/models/Chat.v2";
import { auditChatRead } from "../utils/firestoreReadAudit";
import {
  getNewestMessageCreatedAt,
  mergeMessagesForStorage,
  writeStoredMessages,
} from "../services/messageStorageCache";

export const useRoomMessagesSubscriber = () => {
  const dispatch = useDispatch();
  const messageUnsubRef = useRef<() => void>();
  const subscribedKeyRef = useRef<string | null>(null);
  const lastFetchedAtRef = useRef<Record<string, number>>({});
  const messagesRef = useRef<any[]>([]);
  const EXPIRY_MS = 60 * 1000 * 3; // 3 minute

  const { activeTab } = useSelector((state: RootState) => state.ChatV2);
  const { collaborationsById, selectedCollaborationId, selectedRoomId } =
    useSelector(
      (state: RootState) =>
        state.ChatV2[activeTab] ?? {
          collaborationOrder: [],
          collaborationsById: {},
          hasMore: false,
          isLoading: false,
          selectedCollaborationId: null,
          selectedRoomId: null,
        },
      shallowEqual
    );

  const selectedRoom: Room | undefined =
    collaborationsById?.[selectedCollaborationId]?.roomsModel?.find(
      (r) => r.id === selectedRoomId
    ) ?? collaborationsById?.[selectedCollaborationId]?.roomsModel?.[0];

  const messages = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.messages ?? []
  );

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!selectedCollaborationId || !selectedRoomId || !selectedRoom) return;

    const key = `${selectedCollaborationId}_${selectedRoomId}`;
    const alreadyFetched = Array.isArray(messages) && messages.length > 0;
    const newestCreatedAt = getNewestMessageCreatedAt(messages);
    const newestCreatedAtKey = newestCreatedAt
      ? `${newestCreatedAt.seconds}_${newestCreatedAt.nanoseconds ?? 0}`
      : "none";
    const subscriptionKey = `${key}_${newestCreatedAtKey}`;
    const lastFetchedAt = lastFetchedAtRef.current[key] ?? 0;
    const now = Date.now();
    const isExpired = now - lastFetchedAt > EXPIRY_MS;

    if (messageUnsubRef.current && subscribedKeyRef.current === subscriptionKey) {
      auditChatRead({
        event: "messages_listener_skip_same_key",
        source: "room_selection_same_key",
        collaborationId: selectedCollaborationId,
        roomId: selectedRoomId,
        conversationId: selectedRoom.conversationId,
        estimatedReads: 0,
        details: {
          subscriptionKey,
          localMessages: messages.length,
        },
      });
      return;
    }

    if (!alreadyFetched || isExpired) {
      auditChatRead({
        event: "messages_fetch_trigger",
        source: "room_selection_listener_initial",
        collaborationId: selectedCollaborationId,
        roomId: selectedRoomId,
        conversationId: selectedRoom?.conversationId,
        estimatedReads: 0,
        details: {
          alreadyFetched,
          isExpired,
          localMessages: messages.length,
          newestCreatedAt: newestCreatedAtKey,
        },
      });
      dispatch(
        chatV2.setMessagesLoading({
          collaborationId: selectedCollaborationId,
          roomId: selectedRoomId,
          isLoading: true,
        })
      );
      lastFetchedAtRef.current[key] = now;
    }

    if (messageUnsubRef.current) {
      auditChatRead({
        event: "messages_listener_unsubscribe",
        source: "room_selection_replace",
        collaborationId: selectedCollaborationId,
        roomId: selectedRoomId,
        estimatedReads: 0,
      });
      messageUnsubRef.current();
    }

    auditChatRead({
      event: "messages_listener_subscribe",
      source: newestCreatedAt ? "room_selection_newer" : "room_selection",
      collaborationId: selectedCollaborationId,
      roomId: selectedRoomId,
      conversationId: selectedRoom.conversationId,
      estimatedReads: 0,
      details: {
        subscriptionKey,
        afterCreatedAt: Boolean(newestCreatedAt),
        localMessages: messages.length,
      },
    });

    const unsub = subscribeToMessagesForRoom(
      selectedCollaborationId,
      selectedRoomId,
      selectedRoom.conversationId,
      (message, type) => {
        if (type === "added") {
          dispatch(
            chatV2.addNewMessage({
              collaborationId: selectedCollaborationId,
              roomId: selectedRoomId,
              message,
            })
          );
        } else if (type === "modified") {
          dispatch(
            chatV2.updateMessage({
              collaborationId: selectedCollaborationId,
              roomId: selectedRoomId,
              message,
            })
          );
        }
      },
      ({ messages, lastVisible, hasMore }) => {
        if (newestCreatedAt) {
          writeStoredMessages(
            selectedCollaborationId,
            selectedRoomId,
            mergeMessagesForStorage(messagesRef.current, messages)
          );
          messages.forEach((message) => {
            dispatch(
              chatV2.addNewMessage({
                collaborationId: selectedCollaborationId,
                roomId: selectedRoomId,
                message,
              })
            );
          });
          dispatch(
            chatV2.setMessagesLoading({
              collaborationId: selectedCollaborationId,
              roomId: selectedRoomId,
              isLoading: false,
            })
          );
          return;
        }

        dispatch(chatV2.setMessagesForRoom({
          collaborationId: selectedCollaborationId,
          roomId: selectedRoomId,
          messages,
          lastVisible,
          hasMore,
        }));
      },
      newestCreatedAt
    );

    messageUnsubRef.current = unsub;
    subscribedKeyRef.current = subscriptionKey;

    return () => {
      if (messageUnsubRef.current) {
        auditChatRead({
          event: "messages_listener_unsubscribe",
          source: "room_selection_cleanup",
          collaborationId: selectedCollaborationId,
          roomId: selectedRoomId,
          estimatedReads: 0,
        });
        messageUnsubRef.current();
        messageUnsubRef.current = undefined;
        subscribedKeyRef.current = null;
      }
    };
  }, [
    dispatch,
    selectedCollaborationId,
    selectedRoomId,
    selectedRoom?.conversationId,
  ]);

  useEffect(() => {
    const key = `${selectedCollaborationId}_${selectedRoomId}`;
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        selectedCollaborationId &&
        selectedRoomId &&
        selectedRoom
      ) {
        const now = Date.now();
        const lastFetchedAt = lastFetchedAtRef.current[key] ?? 0;
        const isExpired = now - lastFetchedAt > EXPIRY_MS;

        if (isExpired) {
          const currentMessages = messagesRef.current ?? [];
          const newestMessage = currentMessages.reduce((newest, message) => {
            const currentSeconds = message?.createdAt?.seconds ?? 0;
            const newestSeconds = newest?.createdAt?.seconds ?? 0;
            return currentSeconds > newestSeconds ? message : newest;
          }, currentMessages[0]);

          auditChatRead({
            event: "messages_fetch_trigger",
            source: newestMessage?.createdAt
              ? "visibility_new_messages"
              : "visibility_refresh",
            collaborationId: selectedCollaborationId,
            roomId: selectedRoomId,
            conversationId: selectedRoom.conversationId,
            estimatedReads: 0,
            details: {
              lastFetchedAt,
              expiryMs: EXPIRY_MS,
              localMessages: currentMessages.length,
              newestMessageId: newestMessage?.id,
            },
          });

          if (newestMessage?.createdAt) {
            dispatch(
              fetchNewMessagesForRoom({
                collaborationId: selectedCollaborationId,
                roomId: selectedRoomId,
                conversationId: selectedRoom.conversationId,
                afterCreatedAt: newestMessage.createdAt,
              })
            );
          } else {
            dispatch(
              fetchMessagesForRoom({
                collaborationId: selectedCollaborationId,
                roomId: selectedRoomId,
                conversationId: selectedRoom.conversationId,
              })
            );
          }

          lastFetchedAtRef.current[key] = now;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    dispatch,
    selectedCollaborationId,
    selectedRoomId,
    selectedRoom?.conversationId,
  ]);
};
